import {
  MultiSelectWithTag,
  SearchableSelect,
} from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  closeModal,
  DateInput,
  Modal,
  openModal,
  Select,
  TextInput,
} from "risa-oasis-ui_v2";
import {
  normalizeQueryBuilderOptions,
  QueryBuilderConfig,
  QueryCondition,
} from "../../data-model/queryBuilder";
import { CrossIcon } from "../../svg/cross-icon";
import {
  formatDateInputValueForStorage,
  formatTimestampValueForDateInput,
  getFieldDef,
  getOperatorDef,
  resolveOperatorValueForUi,
} from "./queryBuilderUtils";
import { useQueryBuilderValueSearch } from "./useQueryBuilderValueSearch";

const DELETE_CONDITION_MODAL_ID = "delete-condition-confirmation-modal";

interface QueryBuilderConditionRowProps {
  condition: QueryCondition;
  config: QueryBuilderConfig;
  onFieldChange: (conditionId: string, fieldValue: string) => void;
  onOperatorChange: (
    conditionId: string,
    operatorValue: string,
    fieldValue: string,
  ) => void;
  onValueChange: (conditionId: string, newValue: string | string[]) => void;
  onRemove: (conditionId: string) => void;
}

const QueryBuilderConditionRow = ({
  condition,
  config,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
}: QueryBuilderConditionRowProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (showDeleteConfirm) {
      openModal(DELETE_CONDITION_MODAL_ID);
    }
  }, [showDeleteConfirm]);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onRemove(condition.id);
    closeModal(DELETE_CONDITION_MODAL_ID);
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    closeModal(DELETE_CONDITION_MODAL_ID);
    setShowDeleteConfirm(false);
  };

  const fieldDef = getFieldDef(config, condition.field);
  const operatorDef = fieldDef
    ? getOperatorDef(fieldDef, condition.operator)
    : undefined;
  const resolvedOperatorValue =
    fieldDef != null
      ? resolveOperatorValueForUi(fieldDef, condition.operator)
      : condition.operator;

  const fieldOptions = normalizeQueryBuilderOptions(
    config.fields.map((f) => ({ value: f.value, label: f.label })),
  );

  const operatorOptions = fieldDef
    ? normalizeQueryBuilderOptions(
        fieldDef.operators.map((op) => ({ value: op.value, label: op.label })),
      )
    : [];

  const valueOptions =
    fieldDef?.valueOptions ?? operatorDef?.valueOptions ?? [];
  const baseValueOptionsAsMultiSelect = normalizeQueryBuilderOptions(
    valueOptions.map((o) => ({ value: o.value, label: o.label })),
  ).map((o) => ({ id: o.value, label: o.label }));

  const selectedValues = Array.isArray(condition.value)
    ? condition.value
    : typeof condition.value === "string" && condition.value
      ? [condition.value]
      : [];
  const existingOptionIds = new Set(
    baseValueOptionsAsMultiSelect.map((o) => o.id),
  );
  const missingSelectedOptions = selectedValues
    .filter((v) => !existingOptionIds.has(v))
    .map((v) => ({ id: v, label: v }));
  const valueOptionsAsMultiSelect = [
    ...baseValueOptionsAsMultiSelect,
    ...missingSelectedOptions,
  ];

  const searchProps = useQueryBuilderValueSearch({
    columnName: condition.field,
    initialOptions: valueOptionsAsMultiSelect,
    resetDeps: [condition.field, condition.operator],
  });

  const conditionValueToString = (v: string | string[]): string => {
    if (Array.isArray(v)) return v[0] ?? "";
    return v ?? "";
  };

  const renderTimestampDateInput = () => {
    const stored = conditionValueToString(condition.value);
    return (
      <div className="-mt-1.5 w-full">
        <DateInput
          id={`value-${condition.id}`}
          label=""
          format="MM/DD/YYYY"
          defaultValue={formatTimestampValueForDateInput(stored)}
          onChange={(data: { value: string }) =>
            onValueChange(
              condition.id,
              data.value
                ? formatDateInputValueForStorage(
                    moment(data.value).isValid()
                      ? moment(data.value).format("MM/DD/YYYY")
                      : String(data.value),
                  )
                : "",
            )
          }
        />
      </div>
    );
  };

  const renderValueInput = () => {
    if (!operatorDef) {
      return (
        <div className="w-full">
          <TextInput
            label="Value"
            id={`value-${condition.id}`}
            hideLabel={true}
            placeholder="Enter value..."
            defaultValue={conditionValueToString(condition.value)}
            onChange={(data: { value: string }) =>
              onValueChange(condition.id, data.value)
            }
          />
        </div>
      );
    }

    switch (operatorDef.valueInputType) {
      case "none":
        return null;
      case "multi-select":
        return (
          <MultiSelectWithTag
            dropdownWidth="5rem"
            className="w-full"
            {...searchProps}
            value={Array.isArray(condition.value) ? condition.value : []}
            onChange={(vals) => onValueChange(condition.id, vals)}
            placeholder="Select value..."
          />
        );
      case "single-select":
        return (
          <MultiSelectWithTag
            dropdownWidth="5rem"
            className="w-full"
            {...searchProps}
            value={
              typeof condition.value === "string" && condition.value
                ? [condition.value]
                : []
            }
            onChange={(ids) => onValueChange(condition.id, ids[0] ?? "")}
            placeholder="Select value..."
          />
        );
      case "number":
        return (
          <div className="w-full">
            <TextInput
              label="Value"
              id={`value-${condition.id}`}
              hideLabel={true}
              type="number"
              placeholder="0"
              defaultValue={conditionValueToString(condition.value)}
              onChange={(data: { value: string }) =>
                onValueChange(condition.id, data.value)
              }
            />
          </div>
        );
      case "date":
        return renderTimestampDateInput();
      case "text":
        break;
      default:
        break;
    }

    const dataType = fieldDef?.dataType?.toUpperCase() ?? "";

    const freeTextOperators = new Set([
      "contains",
      "does_not_contain",
      "contains_any_of",
      "does_not_contain_any_of",
      "starts_with",
      "does_not_start_with",
      "ends_with",
      "does_not_end_with",
      "equals",
      "not_equals",
      "is",
      "is_not",
    ]);

    if (dataType === "STRING" || dataType === "") {
      if (
        operatorDef.valueInputType === "text" ||
        freeTextOperators.has(operatorDef.value)
      ) {
        return (
          <div className="w-full">
            <TextInput
              label="Value"
              id={`value-${condition.id}`}
              hideLabel={true}
              placeholder="Enter value..."
              defaultValue={conditionValueToString(condition.value)}
              onChange={(data: { value: string }) =>
                onValueChange(condition.id, data.value)
              }
            />
          </div>
        );
      }
      return (
        <MultiSelectWithTag
          dropdownWidth="5rem"
          className="w-full"
          {...searchProps}
          value={Array.isArray(condition.value) ? condition.value : []}
          onChange={(vals) => onValueChange(condition.id, vals)}
          placeholder="Select value..."
        />
      );
    }
    if (
      dataType === "INTEGER" ||
      dataType === "FLOAT" ||
      dataType === "FLOAT64" ||
      dataType === "FLOAT32" ||
      dataType === "INT64" ||
      dataType === "INT32"
    ) {
      return (
        <div className="w-full">
          <TextInput
            label="Value"
            id={`value-${condition.id}`}
            hideLabel={true}
            type="number"
            placeholder="0"
            defaultValue={conditionValueToString(condition.value)}
            onChange={(data: { value: string }) =>
              onValueChange(condition.id, data.value)
            }
          />
        </div>
      );
    }
    if (dataType === "BOOLEAN") {
      return (
        <MultiSelectWithTag
          dropdownWidth="5rem"
          className="w-full"
          {...searchProps}
          value={
            typeof condition.value === "string" && condition.value
              ? [condition.value]
              : []
          }
          onChange={(ids) => onValueChange(condition.id, ids[0] ?? "")}
          placeholder="Select value..."
        />
      );
    }

    return (
      <div className="w-full">
        <TextInput
          label="Value"
          id={`value-${condition.id}`}
          hideLabel={true}
          placeholder="Enter value..."
          defaultValue={conditionValueToString(condition.value)}
          onChange={(data: { value: string }) =>
            onValueChange(condition.id, data.value)
          }
        />
      </div>
    );
  };

  return (
    <div className="flex w-full items-center gap-3 rounded-e rounded-s border border-primaryGray-15 bg-white px-3 py-2">
      {/* <div className="flex-shrink-0 cursor-grab text-gray-400">
        <HamburgerIcon />
      </div> */}
      <div className="flex min-w-0 flex-1 flex-row gap-2">
        <div className="min-w-0 flex-1">
          <SearchableSelect
            id={`field-${condition.id}`}
            label=""
            options={fieldOptions}
            value={condition.field}
            onOptionChange={(data) => onFieldChange(condition.id, data.value)}
            placeholder="Select field..."
            defaultValue={condition.field}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Select
            id={`operator-${condition.id}`}
            label=""
            options={operatorOptions}
            value={resolvedOperatorValue}
            onOptionChange={(data) =>
              onOperatorChange(condition.id, data.value, condition.field)
            }
            placeholder="Select operator..."
            defaultValue={resolvedOperatorValue}
          />
        </div>
        <div className="mt-1.5 min-w-0 flex-1">{renderValueInput()}</div>
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={handleDeleteClick}
        className="flex-shrink-0 cursor-pointer rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
        aria-label="Remove condition"
      >
        <CrossIcon width="16" height="16" fillColor="#9ca3af" />
      </div>
      {showDeleteConfirm && (
        <Modal
          dialogId={DELETE_CONDITION_MODAL_ID}
          title="Delete Condition"
          saveButtonText="Yes, Delete"
          cancelText="Cancel"
          onSave={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          onClose={handleDeleteCancel}
        >
          <div className="flex flex-col items-center justify-center gap-3 py-2">
            <div className="text-center text-sm text-primaryGray-3">
              Are you sure you want to delete this condition? This action cannot
              be undone.
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default QueryBuilderConditionRow;
